import React, { PureComponent } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import getYear from 'date-fns/get_year';
import getMonth from 'date-fns/get_month';
import format from 'date-fns/format';
import { CalendarModel, CalendarType } from '@nucleo/calendar';
import { OccurrenceCalendar } from '@gravity/occurrence-calendar';
import WidgetState from '@gravity/widget-state';
import { FrontEndError } from '@gravity/g-axios';

const Wrapper = styled.div.attrs({
  className: 'stx-AvailabilityCalendarWidget',
})`
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 100%;
  vertical-align: baseline;
  box-sizing: border-box;
  text-align: left;
`;

/**
 * The occurrence calendar widget display product availabilities in a mini calendar
 */
class AvailabilityCalendarWidget extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selectedDay: null,
    };

    this.changeMonth = this.changeMonth.bind(this);
    this.changeProduct = this.changeProduct.bind(this);
    this.changeDay = this.changeDay.bind(this);
  }
  componentWillMount() {
    this.props.loadMonths(this.props.productId);
  }
  componentDidCatch(error, info) {
    // You can also log the error to an error reporting service
    this.props.logError(FrontEndError.RenderWidgetError, error, info);
  }

  changeMonth(monthDate) {
    const monthName = format(monthDate, 'YYYY-MM');
    if (!this.props.calendar.sectionByMonth[monthName]) {
      return this.props.loadMonth(getYear(monthDate), getMonth(monthDate));
    }
  }

  changeProduct(productId) {
    return this.props.loadMonths(productId);
  }

  /**
   * Method to be called when a day is selected in the calendar
   * @param {string} day, selected day
   */
  changeDay(day) {
    this.setState({ selectedDay: day });
    this.props.changeDay(day);
  }

  render() {
    const calendarData = this.props.calendar;
    const { ERROR } = calendarData;
    const hasData = CalendarModel.hasData(calendarData) || CalendarModel.hasMonths(calendarData);
    // if an occurrenceId is passed, try to load it
    let curState;
    // only show error, do not show loading
    if (ERROR || this.props.configError) {
      curState = 'ERROR';
    }
    return (
      <Wrapper>
        <WidgetState state={curState} code={this.props.configErrorCode} />
        {hasData && (
          <OccurrenceCalendar
            calendar={calendarData}
            changeMonth={this.changeMonth}
            changeProduct={this.changeProduct}
            onClickDay={this.props.clickable ? this.changeDay : undefined}
            selectedDay={this.state.selectedDay}
          />
        )}
      </Wrapper>
    );
  }
}

AvailabilityCalendarWidget.propTypes = {
  /**
   * Global config : contains the point of sales that will provide information. To be passed in the STX.widgets.start function.
   */
  hostname: PropTypes.string.isRequired,
  /**
   * Global config : contains the api key related to the point of sales. To be passed in the STX.widgets.start function.
   */
  apiKey: PropTypes.string.isRequired,
  /**
   * Global config : Explicitely pass the language to the widget that will be loaded. If not passed, the default language of the institution will be used
   */
  language: PropTypes.string,
  /**
   * The product id to display
   */
  productId: PropTypes.number.isRequired,
  /**
   *  @IGNORE generated, define if an error happend at the config level.
   */
  configError: PropTypes.bool,
  /**
   * @IGNORE generated, define what kind of error happened at the config level
   */
  configErrorCode: PropTypes.string,
  /**
   * @IGNORE generated calendar after a load of data
   */
  calendar: CalendarType,
  /**
   * @IGNORE generated function to load the calendar
   */
  loadMonth: PropTypes.func.isRequired,
  /**
   * @IGNORE generated function to load the range of date
   */
  loadMonths: PropTypes.func.isRequired,
  /**
   * @IGNORE generated function to log in case of error
   */
  logError: PropTypes.func.isRequired,
  /**
   * @IGNORE If the widget is going to allow that the calendar is clickable or use a redirect
   */
  clickable: PropTypes.bool,
  /**
   * @IGNORE action to be called when change the day
   */
  changeDay: PropTypes.func,
};
AvailabilityCalendarWidget.defaultProps = {
  calendar: undefined,
  configError: false,
  configErrorCode: undefined,
  clickable: false,
  changeDay: undefined,
};

export default AvailabilityCalendarWidget;
